/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 10/11/23
 */

import { Dialog } from '@/components/Dialog';
import { Button, Heading, Radio, RadioGroup, Stack, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import { usePutRequest } from '@/utils/useStatefulRequests';
import { Assignment, Reason } from '@/wpt-lib/interfaces/Assignment';
import { UpdateAssignmentStateDTO } from '@/components/pages/Admin/Workload/WorkloadEditor';
import { UserStatusProps } from '@/components/pages/Admin/UserStatus/AssignmentStatusEditor';
import LabeledTextarea from '@/components/LabeledTextarea/LabeledTextarea';
import UserStatusHistoryList from '@/components/pages/Admin/UserStatus/UserStatusHistoryList';
import config from '@/config';

const AssignmentStatusDialog = ({ assignment, currentAssignment, refresh }: UserStatusProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [reason, setReason] = useState<string>('');
    const {
        error,
        loading,
        makeRequest: activateDeactivateAssignment,
    } = usePutRequest<Assignment, UpdateAssignmentStateDTO>();

    const handleActivateDeactivateAssignment = async () => {
        try {
            await activateDeactivateAssignment(
                `assignment/state/${assignment?.id}`,
                {
                    inactive: !assignment?.inactive,
                    reason,
                },
                {
                    params: {
                        assignment: currentAssignment.id,
                    },
                },
            );
            setReason('');
            refresh();
            setIsOpen(false);
        } catch (e) {
            // handle by hook
        }
    };
    return (
        <VStack alignItems="flex-start" justifyContent="center">
            <Text>
                Assignment {assignment.assignmentNumber} - {assignment.name}:{' '}
                <Text as="em">{assignment.inactive ? 'Deactivated' : 'Active'}</Text>
            </Text>
            <UserStatusHistoryList assignment={assignment} currentAssignment={currentAssignment} />
            {config.featureFlags.enableReasonTextBox || assignment.inactive ? (
                <LabeledTextarea
                    label={`Reason to ${assignment?.inactive ? 'Activate' : 'Deactivate'}: `}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    width="100%"
                />
            ) : (
                <>
                    <Heading as="h3" size="small" color="blue.500">{`Reason to ${
                        assignment?.inactive ? 'Activate' : 'Deactivate'
                    } `}</Heading>
                    <RadioGroup id="reason-input" value={reason} onChange={(value) => setReason(value)}>
                        <Stack direction="column" alignItems="flex-start">
                            {Object.values(Reason).map((value) => {
                                return (
                                    <Radio key={value} value={value} size={'lg'} cursor={'pointer'}>
                                        {value}
                                    </Radio>
                                );
                            })}
                        </Stack>
                    </RadioGroup>
                </>
            )}
            <Dialog
                colorScheme="blue"
                isOpen={isOpen}
                isLoading={loading}
                dialogHeader={`${assignment?.inactive ? 'Activate' : 'Deactivate'} assignment ${
                    assignment.assignmentNumber
                } - ${assignment.name} `}
                dialogConfirmLabel={assignment?.inactive ? 'Activate' : 'Deactivate'}
                onClose={() => setIsOpen(false)}
                errorMsg={
                    error
                        ? `Could not ${assignment?.inactive ? 'activate' : 'deactivate'} assignment. ${
                              assignment?.inactive
                                  ? 'Check if assignment is active in NEO. If not active in NEO, assignment activation will fail.'
                                  : ''
                          }`
                        : undefined
                }
                dialogAction={handleActivateDeactivateAssignment}
            >
                <Text>
                    Are you sure you want to {assignment?.inactive ? 'activate' : 'deactivate'} this assignment?{' '}
                    {!assignment.inactive &&
                        'Once deactivated all non-endorsed workplans for this assignment are closed.'}
                </Text>
            </Dialog>
            <Button
                colorScheme="blue"
                maxWidth="160"
                onClick={() => setIsOpen(true)}
                disabled={loading || !reason}
                marginTop={5}
            >
                {assignment?.inactive ? 'Activate' : 'Deactivate'}
            </Button>
        </VStack>
    );
};

export default AssignmentStatusDialog;
